import { Banner, BannerType } from '../../../../components/Banner/Banner'
import { museumRecordingUrlErrorMessages } from '../ErtPreprocessor'
import {
  type EngagementRecordingsDisallowedReason,
  type Activity,
  type MonitoringSpecialistAssignmentOutdoorActivity,
  type OutdoorEngagement,
  AbandonedOutdoorActivityResultType,
  AbandonedOutdoorActivityResultReason,
  EngagementAssignedOutdoorActivityResultType,
  MonitoringSpecialistAssignmentOutdoorActivityType
} from '../../../../api/models'
import { DateTime } from 'luxon'
import { getDisallowedReasonMessage, noSpecialistAssignedTitle, unhandledCauseMessages } from './utils/messagesUtils'

// Camera recordings are only kept this long - older footage no longer exists.
const RECORDING_RETENTION_DAYS = 30

type ErtInspectErrorBannerProps = {
  engagement: OutdoorEngagement
  activities: Activity[]
  errorCode: number
  disallowedReason?: EngagementRecordingsDisallowedReason
}

// Only two abandonment reasons can reach an un-assignment activity. Every other
// AbandonedOutdoorActivityResultReason (Logout, WindowClose, SpecialistMismatch, ...) reason
// only happen when an engagement is assigned to a specialist, which this component doesn't handle.
// Both reachable reasons tell a reviewer the same thing: the engagement ended while the assignment
// sat unclaimed.
const abandonedAssignmentMessage = 'The engagement ended before a specialist picked up this assignment.'

const abandonedReasonMessages: Partial<Record<AbandonedOutdoorActivityResultReason, string>> = {
  // the engagement resolved (obsolete, customer disarm, AI clear, ...) with the assignment still pending.
  [AbandonedOutdoorActivityResultReason.EngagementAbandoned]: abandonedAssignmentMessage,
  // something cleared the engagement while this assignment was still open.
  [AbandonedOutdoorActivityResultReason.NotCompleteWhenCleared]: abandonedAssignmentMessage
}

const fallbackAbandonedMessage = 'The assignment ended without a specialist completing it.'

const isAssignmentActivity = (activity: Activity): activity is MonitoringSpecialistAssignmentOutdoorActivity =>
  activity.type === MonitoringSpecialistAssignmentOutdoorActivityType.MonitoringSpecialistAssignment

// An engagement accumulates a new assignment activity every time a specialist unassigns, and duplicates
// get completed as Superseded - so the outcome that matters is the newest one
const getLatestAssignmentActivity = (activities: Activity[]): MonitoringSpecialistAssignmentOutdoorActivity | undefined =>
  activities
    .filter(isAssignmentActivity)
    .reduce<MonitoringSpecialistAssignmentOutdoorActivity | undefined>(
      (latest, activity) => (!latest || activity.created.timestamp > latest.created.timestamp ? activity : latest),
      undefined
    )

// The QA policy gate: an engagement qualifies for video review only if it reached a specialist at least
// once. Any assignment activity that completed as EngagementAssigned proves that happened.
const wasEverAssignedToSpecialist = (activities: Activity[]): boolean =>
  activities
    .filter(isAssignmentActivity)
    .some(activity => activity.completed?.result?.type === EngagementAssignedOutdoorActivityResultType.EngagementAssigned)

// How the engagement's assignment ended, when it ended without the specialist finishing.
const getAbandonedReason = (activities: Activity[]): string | undefined => {
  const result = getLatestAssignmentActivity(activities)?.completed?.result
  if (result?.type !== AbandonedOutdoorActivityResultType.Abandoned) return undefined
  return (result.reason ? abandonedReasonMessages[result.reason] : undefined) ?? fallbackAbandonedMessage
}

/**
 * Banner shown at the top level of ERT Inspect when there is no footage to review.
 *
 * Causes are ranked so the most explanatory one wins, because several are true at once on the same
 * engagement (a never-assigned engagement from last year is both ineligible AND past retention AND
 * likely to 404). Reporting the wrong one sends the reviewer looking for footage that never existed.
 *
 * 1. Permission - the customer or the reviewer's role blocks access. Nothing else matters if so.
 * 2. QA eligibility - no specialist was ever assigned, so the engagement was never eligible for video
 *    review at all. Per the monitoring QA policy this holds from day one, which is why it outranks
 *    retention: "an engagement is qualified for potential video and imagery review only if it has been
 *    assigned to at least one monitoring specialist."
 * 3. Retention - footage existed and has aged out.
 * 4. Transport - the /recordings call itself failed; a retry may help.
 */
export const ErtInspectErrorBanner = ({ engagement, activities, errorCode, disallowedReason }: ErtInspectErrorBannerProps) => {
  const ageInDays = DateTime.now().diff(DateTime.fromISO(engagement.createdTimestamp), 'days').days
  const isPastRetention = ageInDays > RECORDING_RETENTION_DAYS

  let title: string
  let body: string | undefined
  // One test id per tier, so which cause won is observable rather than inferred from the copy.
  let testId: string

  if (disallowedReason) {
    title = getDisallowedReasonMessage(disallowedReason)
    testId = 'recordings-not-allowed-banner'
  } else if (activities.length > 0 && !wasEverAssignedToSpecialist(activities)) {
    // Guarded on a non-empty list: an activities fetch that failed or came back empty would otherwise
    // make every engagement look never-assigned and swallow the real cause.
    const resolutionCategory = engagement.result?.resolution.category
    title = noSpecialistAssignedTitle
    body = resolutionCategory ? unhandledCauseMessages[resolutionCategory] : undefined
    testId = 'recordings-not-eligible-banner'
  } else if (isPastRetention && errorCode === 404) {
    title = `Recordings expired after ${RECORDING_RETENTION_DAYS} days`
    testId = 'recordings-expired-banner'
  } else {
    title = museumRecordingUrlErrorMessages[errorCode] ?? 'Recordings are unavailable for this engagement.'
    // Only when the fetch itself succeeded (but returned no clips) is "how the handling ended"
    // explanatory - next to an HTTP error it reads as the cause of the failure, which it never is.
    body = !errorCode ? getAbandonedReason(activities) : undefined
    testId = 'recordings-error-banner'
  }

  return <Banner type={BannerType.Error} title={title} body={body} sx={{ marginY: 'var(--spacing-l)' }} data-testid={testId} />
}
